import React, { useEffect, useState } from 'react';
const makeIcon = (symbol) => function Icon({ className = '' }) { return <span className={className} aria-hidden="true">{symbol}</span>; };
const FaArrowRight = makeIcon('→');
const FaBars = makeIcon('☰');
const FaCheck = makeIcon('✓');
const FaEnvelope = makeIcon('✉');
const FaFacebookF = makeIcon('f');
const FaInstagram = makeIcon('◎');
const FaLinkedinIn = makeIcon('in');
const FaMapMarkerAlt = makeIcon('⌖');
const FaPhoneAlt = makeIcon('☎');
const FaQuoteLeft = makeIcon('❝');
const FaTimes = makeIcon('×');
const FaWhatsapp = makeIcon('◉');

const navItems = [
  ['Home', 'home'],
  ['About', 'about'],
  ['Products', 'products'],
  ['Services', 'services'],
  ['Reviews', 'reviews'],
  ['Contact', 'contact'],
];

const products = [
  {
    name: 'Business Website Development',
    description: 'Professional responsive websites designed to build trust, generate enquiries, and perform smoothly across every device.',
    image: '/images/product-packaging.svg',
    tag: 'Popular',
  },
  {
    name: 'E-commerce & Landing Pages',
    description: 'High-converting product pages, campaign landing pages, and streamlined online storefront experiences.',
    image: '/images/product-office.svg',
    tag: 'Business',
  },
  {
    name: 'Website Care & Optimization',
    description: 'Speed improvements, content updates, security checks, and ongoing technical maintenance for reliable websites.',
    image: '/images/product-cleaning.svg',
    tag: 'Essential',
  },
];

const services = [
  {
    number: '01',
    title: 'Custom Website Development',
    text: 'Responsive business websites tailored to your goals, audience, brand identity, and customer journey.',
  },
  {
    number: '02',
    title: 'UI/UX & Front-end Design',
    text: 'Clear interfaces, thoughtful user journeys, and polished front-end experiences designed for usability and conversions.',
  },
  {
    number: '03',
    title: 'Maintenance & Growth Support',
    text: 'Dependable post-launch support, performance optimization, updates, and practical guidance as your business grows.',
  },
];

const reviews = [
  {
    name: 'Ankit Mehra',
    role: 'Founder, NorthPeak Consulting',
    text: 'WebNinja understood our business quickly and delivered a clean, fast website that feels professional on every device.',
  },
  {
    name: 'Priya Nair',
    role: 'Marketing Lead, BloomCraft',
    text: 'WEBNINJA ENTERPRISES transformed our ideas into a polished landing page that improved the quality of our enquiries.',
  },
  {
    name: 'Rahul Deshmukh',
    role: 'Director, UrbanEdge Services',
    text: 'Clear communication, strong design sense, and dependable support after launch. The entire process was smooth.',
  },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  const submitForm = (event) => {
    event.preventDefault();
    setSubmitted(true);
    event.currentTarget.reset();
  };

  return (
    <div className="site-shell">
      <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
        <div className="container header__inner">
          <a className="brand" href="#home" onClick={closeMenu} aria-label="WEBNINJA ENTERPRISES home">
            <img src="/images/logo.svg" alt="WEBNINJA ENTERPRISES" />
          </a>

          <button
            className="menu-toggle"
            type="button"
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`nav ${menuOpen ? 'nav--open' : ''}`} aria-label="Primary navigation">
            {navItems.map(([label, id]) => (
              <a key={id} href={`#${id}`} onClick={closeMenu}>{label}</a>
            ))}
            <a className="button button--small" href="#contact" onClick={closeMenu}>Get a Quote</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="hero" id="home">
          <div className="hero__orb hero__orb--one" />
          <div className="hero__orb hero__orb--two" />
          <div className="container hero__grid">
            <div className="hero__content reveal">
              <span className="eyebrow">Web experiences engineered for growth.</span>
              <h1>High-impact websites and digital solutions built for ambitious businesses.</h1>
              <p>
                WEBNINJA ENTERPRISES helps startups, local businesses, and growing brands launch fast, responsive, and conversion-focused digital experiences.
              </p>
              <div className="hero__actions">
                <a className="button" href="#products">Explore Solutions <FaArrowRight /></a>
                <a className="text-link" href="#contact">Talk to our team</a>
              </div>
              <div className="hero__trust">
                <div><strong>500+</strong><span>Projects delivered</span></div>
                <div><strong>97%</strong><span>Client satisfaction</span></div>
                <div><strong>24h</strong><span>Response commitment</span></div>
              </div>
            </div>
            <div className="hero__visual reveal">
              <div className="hero-card hero-card--main">
                <img src="/images/hero-business.svg" alt="Business supply and service illustration" />
              </div>
              <div className="hero-card hero-card--floating">
                <span className="hero-card__icon"><FaCheck /></span>
                <div><strong>Responsive by default</strong><small>Optimized for every screen</small></div>
              </div>
            </div>
          </div>
        </section>

        <section className="section about" id="about">
          <div className="container about__grid">
            <div className="about__image-wrap reveal">
              <img src="/images/about-team.svg" alt="WEBNINJA ENTERPRISES team illustration" />
              <div className="experience-badge"><strong>8+</strong><span>Years building online</span></div>
            </div>
            <div className="section-copy reveal">
              <span className="eyebrow">About WebNinja</span>
              <h2>Creative thinking, clean code, and dependable delivery.</h2>
              <p>
                We create modern digital experiences for startups, service businesses, retailers, and growing brands. Every website is built around clarity, speed, usability, and measurable business outcomes.
              </p>
              <ul className="check-list">
                <li><FaCheck /> Mobile-first responsive design</li>
                <li><FaCheck /> Clear scope and transparent delivery</li>
                <li><FaCheck /> Reliable post-launch support</li>
              </ul>
              <a className="button button--outline" href="#contact">Know More <FaArrowRight /></a>
            </div>
          </div>
        </section>

        <section className="section section--soft" id="products">
          <div className="container">
            <div className="section-heading reveal">
              <div><span className="eyebrow">Our solutions</span><h2>Digital solutions designed to move businesses forward.</h2></div>
              <p>Every solution can be tailored to your brand, audience, features, timeline, and business goals.</p>
            </div>
            <div className="card-grid products-grid">
              {products.map((product) => (
                <article className="product-card reveal" key={product.name}>
                  <div className="product-card__image">
                    <span className="pill">{product.tag}</span>
                    <img src={product.image} alt={product.name} />
                  </div>
                  <div className="product-card__body">
                    <h3>{product.name}</h3>
                    <p>{product.description}</p>
                    <a href="#contact">Start a project <FaArrowRight /></a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section services" id="services">
          <div className="container">
            <div className="section-heading section-heading--center reveal">
              <div><span className="eyebrow">Our services</span><h2>Web services that keep digital growth simple.</h2></div>
              <p>From strategy and design to launch and maintenance, we stay focused on practical results and clear communication.</p>
            </div>
            <div className="services-grid">
              {services.map((service) => (
                <article className="service-card reveal" key={service.number}>
                  <span className="service-card__number">{service.number}</span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  <a href="#contact" aria-label={`Enquire about ${service.title}`}><FaArrowRight /></a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section reviews" id="reviews">
          <div className="container">
            <div className="section-heading reveal">
              <div><span className="eyebrow">Client reviews</span><h2>What clients say about working with WebNinja.</h2></div>
              <p>These sample testimonials can be replaced with verified customer feedback.</p>
            </div>
            <div className="reviews-grid">
              {reviews.map((review) => (
                <article className="review-card reveal" key={review.name}>
                  <FaQuoteLeft className="review-card__quote" />
                  <p>“{review.text}”</p>
                  <div className="review-card__person">
                    <span>{review.name.split(' ').map((name) => name[0]).join('')}</span>
                    <div><strong>{review.name}</strong><small>{review.role}</small></div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section contact" id="contact">
          <div className="container contact__grid">
            <div className="contact__info reveal">
              <span className="eyebrow eyebrow--light">Contact us</span>
              <h2>Let’s build something powerful for your business.</h2>
              <p>Tell us what you need and our team will respond with a clear plan and next steps.</p>
              <div className="contact-list">
                <a href="tel:+918247601856"><span><FaPhoneAlt /></span><div><small>Call us</small><strong>+91 82476 01856</strong></div></a>
                <a href="mailto:admin@webninjaenterprises.online"><span><FaEnvelope /></span><div><small>Email us</small><strong>admin@webninjaenterprises.online</strong></div></a>
                <div><span><FaMapMarkerAlt /></span><div><small>Business name</small><strong>WEBNINJA ENTERPRISES</strong></div></div>
                <div><span><FaMapMarkerAlt /></span><div><small>Business address</small><strong>9-93, Masidu Katta, Narpala, Maseedu Katta, Narpala, Anantapur, ANANTHAPUR, ANDHRA PRADESH, 515425 </strong></div></div>
              </div>
            </div>

            <form className="contact-form reveal" onSubmit={submitForm}>
              <div className="form-row">
                <label>Full name<input type="text" name="name" placeholder="Your name" required /></label>
                <label>Phone number<input type="tel" name="phone" placeholder="+91 82476 01856" required /></label>
              </div>
              <label>Email address<input type="email" name="email" placeholder="you@company.com" required /></label>
              <label>Interested in
                <select name="interest" defaultValue="">
                  <option value="" disabled>Select an option</option>
                  <option>Digital solutions</option>
                  <option>UI/UX design</option>
                  <option>Website maintenance</option>
                  <option>Other requirement</option>
                </select>
              </label>
              <label>Message<textarea name="message" rows="5" placeholder="Tell us about your requirement" required /></label>
              <button className="button button--full" type="submit">Send Enquiry <FaArrowRight /></button>
              {submitted && <p className="form-success" role="status">Thank you! Your sample enquiry has been recorded.</p>}
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__top">
          <div className="footer__brand">
            <img src="/images/logo-light.svg" alt="WEBNINJA ENTERPRISES" />
            <p>Modern websites, thoughtful interfaces, and dependable digital support for growing businesses.</p>
            <div className="socials">
              <a href="https://www.facebook.com" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://www.instagram.com" aria-label="Instagram"><FaInstagram /></a>
              <a href="https://www.linkedin.com" aria-label="LinkedIn"><FaLinkedinIn /></a>
              <a href="https://wa.me/918247601856" aria-label="WhatsApp"><FaWhatsapp /></a>
            </div>
          </div>
          <div className="footer__links">
            <h3>Quick links</h3>
            {navItems.slice(1).map(([label, id]) => <a href={`#${id}`} key={id}>{label}</a>)}
          </div>
          <div className="footer__links">
            <h3>Business hours</h3>
            <p>Monday – Saturday</p>
            <p>9:30 AM – 6:30 PM</p>
            <p>Sunday: Closed</p>
          </div>
        </div>
        <div className="container footer__bottom">
          <span>© {new Date().getFullYear()} WEBNINJA ENTERPRISES. All rights reserved.</span>
          <span>Building sharper digital experiences for business.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
